import { call, put, takeLatest, takeEvery, take, select } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { push } from 'redux-first-history';
import { get, isEmpty } from 'lodash';
import {
  fetchOrganizationMembers,
  createOrganizationMember,
  updateOrganizationMember,
  updateOrganizationMembership,
  removeOrganizationMember,
  sendPasswordReset,
  disableTwoFactorAuthentication,
  fetchOrganizationProjects,
  fetchOrganizationRoles,
  createOrganizationRole,
  updateOrganizationRole,
  fetchOrganizationTeams,
  fetchPermissions,
  fetchDeliveryTargets,
  createDeliveryTarget,
  updateDeliveryTarget,
} from 'api/OrganizationAPI';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { selectFeatureFlags } from 'core/userContext/selectors';
import { FETCH_USER_CONTEXT_RESPONSE } from 'core/userContext/actionTypes';
import * as actionTypes from './actionTypes';
import * as actions from './actions';

export function* getFeatureFlags() {
  try {
    const featureFlags = yield select(selectFeatureFlags);
    if (isEmpty(featureFlags)) {
      yield take(FETCH_USER_CONTEXT_RESPONSE);
      return yield select(selectFeatureFlags);
    }
    return featureFlags;
  } catch (error) {
    return {};
  }
}

// members
export function* fetchOrganizationMembersSaga(action) {
  try {
    const { data } = yield call(fetchOrganizationMembers, action.payload);
    yield put(actions.fetchOrganizationMembersResponse(data));
  } catch (error) {
    yield put(actions.fetchOrganizationMembersError(error.toString()));
  }
}
export function* createOrganizationMemberSaga(action) {
  try {
    const { data } = yield call(createOrganizationMember, action.payload);
    yield put(actions.createOrganizationMemberResponse(data));
    yield put(push(`/members/${data.userId}`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.createOrganizationMemberError(errorMessage));
    yield put(addNegativeToast(`Failed to create member: ${errorMessage}`));
  }
}
export function* updateOrganizationMemberSaga(action) {
  try {
    const data = action.payload;
    yield call(updateOrganizationMember, data);
    yield put(actions.updateOrganizationMemberResponse(data));
    if (!action.bulkAction) {
      yield put(push(`/members/${data.userId}`));
    }
    yield put(addPositiveToast(`Successfully updated ${data.userId}`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.updateOrganizationMemberError(errorMessage));
    yield put(addNegativeToast(`Failed to update member(s): ${errorMessage}`));
  }
}
export function* updateOrganizationMembershipSaga(action) {
  try {
    const data = action.payload;
    yield call(updateOrganizationMembership, data);
    yield put(actions.updateOrganizationMembershipResponse(data));
    if (!action.bulkAction) {
      yield put(push(`/members/${data.userId}`));
    }
    yield put(addPositiveToast(`Successfully updated ${data.userId}`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.updateOrganizationMembershipError(errorMessage));
    yield put(addNegativeToast(`Failed to update membership(s): ${errorMessage}`));
  }
}
export function* removeOrganizationMemberSaga(action) {
  try {
    const { data } = yield call(removeOrganizationMember, action.payload);
    yield put(actions.removeOrganizationMemberResponse(data));
    yield put(addPositiveToast(`Successfully removed ${action.payload.userId}`));
    yield put(push(`/members`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.removeOrganizationMemberError(errorMessage));
    yield put(addNegativeToast(`Failed to remove member: ${errorMessage}`));
  }
}
export function* sendPasswordResetSaga(action) {
  try {
    const { data } = yield call(sendPasswordReset, action.payload);
    yield put(actions.sendPasswordResetResponse(data));
    yield put(addPositiveToast(`Successfully sent email to ${action.payload.userId}`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.sendPasswordResetError(errorMessage));
    yield put(addNegativeToast(`Failed to send email: ${errorMessage}`));
  }
}
export function* disableTwoFactorAuthenticationSaga(action) {
  try {
    const { data } = yield call(disableTwoFactorAuthentication, action.payload);
    yield put(actions.disableTwoFactorAuthenticationResponse(data));
    yield put(
      addPositiveToast(
        `Successfully disabled two factor authentication for ${action.payload.userId}`,
      ),
    );
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.disableTwoFactorAuthenticationError(errorMessage));
    yield put(addNegativeToast(`Failed to disable two factor authentication: ${errorMessage}`));
  }
}

// projects
export function* fetchOrganizationProjectsSaga(action) {
  try {
    const { data } = yield call(fetchOrganizationProjects, action.payload);
    yield put(actions.fetchOrganizationProjectsResponse(data.projects));
  } catch (error) {
    yield put(actions.fetchOrganizationProjectsError(error.toString()));
  }
}

// roles
export function* fetchOrganizationRolesSaga(action) {
  try {
    const { data } = yield call(fetchOrganizationRoles, action.payload);
    yield put(actions.fetchOrganizationRolesResponse(data.roles));
  } catch (error) {
    yield put(actions.fetchOrganizationRolesError(error.toString()));
  }
}
export function* createOrganizationRoleSaga(action) {
  try {
    const data = action.payload;
    yield call(createOrganizationRole, action.payload);
    yield put(actions.createOrganizationRoleResponse(data));
    yield put(addPositiveToast(`Successfully created role ${data.name}`));
    yield put(push(`/roles`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.createOrganizationRoleError(errorMessage));
    yield put(addNegativeToast(`Failed to create role: ${errorMessage}`));
  }
}
export function* updateOrganizationRoleSaga(action) {
  try {
    const data = action.payload;
    yield call(updateOrganizationRole, action.payload);
    yield put(addPositiveToast(`Successfully updated role ${data.name}`));
    yield put(push(`/roles`));
  } catch (error) {
    const errorMessage = get(error, 'response.data.message', error.message);
    yield put(actions.updateOrganizationRoleError(errorMessage));
    yield put(addNegativeToast(`Failed to update role: ${errorMessage}`));
  }
}

// teams
export function* fetchOrganizationTeamsSaga(action) {
  try {
    const { data } = yield call(fetchOrganizationTeams, action.payload);
    yield put(actions.fetchOrganizationTeamsResponse(data.teams));
  } catch (error) {
    yield put(actions.fetchOrganizationTeamsError(error.toString()));
  }
}

// permissions
export function* fetchPermissionsSaga() {
  try {
    const { data } = yield call(fetchPermissions);
    yield put(actions.fetchPermissionsResponse(data.permissions));
  } catch (error) {
    yield put(actions.fetchPermissionsError(error.toString()));
  }
}

// delivery targets
export function* fetchDeliveryTargetsSaga(action) {
  try {
    const { data } = yield call(fetchDeliveryTargets, action.payload);
    yield put(actions.fetchDeliveryTargetsResponse(data));
  } catch (error) {
    yield put(actions.fetchDeliveryTargetsError(error.toString()));
  }
}
export function* createDeliveryTargetSaga(action) {
  try {
    const { data } = yield call(createDeliveryTarget, action.payload);
    yield put(actions.createDeliveryTargetResponse(data));
  } catch (error) {
    yield put(actions.createDeliveryTargetError(error.toString()));
  }
}
export function* updateDeliveryTargetSaga(action) {
  try {
    const { data } = yield call(updateDeliveryTarget, action.payload);
    yield put(actions.updateDeliveryTargetResponse(data));
  } catch (error) {
    yield put(actions.updateDeliveryTargetError(error.toString()));
  }
}

/*----------------------------------------------------------------------------------------------*/

addToRootSaga([
  takeEvery(actionTypes.FETCH_ORGANIZATION_MEMBERS_REQUEST, fetchOrganizationMembersSaga),
  takeEvery(actionTypes.CREATE_ORGANIZATION_MEMBER_REQUEST, createOrganizationMemberSaga),
  takeEvery(actionTypes.UPDATE_ORGANIZATION_MEMBER_REQUEST, updateOrganizationMemberSaga),
  takeEvery(actionTypes.UPDATE_ORGANIZATION_MEMBERSHIP_REQUEST, updateOrganizationMembershipSaga),
  takeEvery(actionTypes.REMOVE_ORGANIZATION_MEMBER_REQUEST, removeOrganizationMemberSaga),
  takeLatest(actionTypes.SEND_PASSWORD_RESET_REQUEST, sendPasswordResetSaga),
  takeEvery(
    actionTypes.DISABLE_TWO_FACTOR_AUTHENTICATION_REQUEST,
    disableTwoFactorAuthenticationSaga,
  ),
  takeLatest(actionTypes.FETCH_ORGANIZATION_PROJECTS_REQUEST, fetchOrganizationProjectsSaga),
  takeLatest(actionTypes.FETCH_ORGANIZATION_ROLES_REQUEST, fetchOrganizationRolesSaga),
  takeEvery(actionTypes.CREATE_ORGANIZATION_ROLE_REQUEST, createOrganizationRoleSaga),
  takeLatest(actionTypes.UPDATE_ORGANIZATION_ROLE_REQUEST, updateOrganizationRoleSaga),
  takeLatest(actionTypes.FETCH_ORGANIZATION_TEAMS_REQUEST, fetchOrganizationTeamsSaga),
  takeLatest(actionTypes.FETCH_PERMISSIONS_REQUEST, fetchPermissionsSaga),
  takeLatest(actionTypes.FETCH_DELIVERY_TARGETS_REQUEST, fetchDeliveryTargetsSaga),
  takeEvery(actionTypes.CREATE_DELIVERY_TARGET_REQUEST, createDeliveryTargetSaga),
  takeLatest(actionTypes.UPDATE_DELIVERY_TARGET_REQUEST, updateDeliveryTargetSaga),
]);
